import { useState } from "react";
import PageBanner from "@/components/PageBanner";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { Helmet } from "react-helmet-async";
import { Link } from "wouter";

export default function RedeemOffer() {
    const [isRedeeming, setIsRedeeming] = useState(false);
    const [success, setSuccess] = useState(false);
    const { toast } = useToast();

    // Extract from query params safely
    const queryParams = new URLSearchParams(window.location.search);
    const email = queryParams.get("email");
    const name = queryParams.get("name");

    const handleRedeem = async () => {
        if (!email || !name) {
            toast({
                title: "Invalid Link",
                description: "We couldn't identify your account from this link.",
                variant: "destructive"
            });
            return;
        }

        setIsRedeeming(true);
        try {
            const response = await fetch("/api/offers/redeem-birthday", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email, name }),
            });

            if (!response.ok) throw new Error("Failed to redeem");

            setSuccess(true);
        } catch (error) {
            toast({
                title: "Error",
                description: "Something went wrong. Please try again later.",
                variant: "destructive"
            });
        } finally {
            setIsRedeeming(false);
        }
    };

    return (
        <>
            <Helmet>
                <title>Redeem Your Birthday Offer - EcoPlace Tours</title>
                <meta name="description" content="Redeem your special 20% off birthday offer right now." />
            </Helmet>

            <div className="max-w-3xl mx-auto px-4 py-16 text-center">
                {!email || !name ? (
                    <div className="bg-red-50 text-red-700 p-8 rounded-lg shadow-sm">
                        <h2 className="text-2xl font-bold mb-4">Invalid Redemption Link</h2>
                        <p>It looks like you clicked an incomplete link. Please check the email we sent you and try clicking the button again.</p>
                    </div>
                ) : success ? (
                    <div className="bg-green-50 text-green-800 p-12 rounded-lg shadow-sm border border-green-200">
                        <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                            <span className="text-4xl">🎉</span>
                        </div>
                        <h2 className="text-3xl font-bold mb-4">Offer Redeemed Successfully!</h2>
                        <p className="text-lg mb-8">Happy Birthday, {name}! Your 20% discount has been activated in our system. Our team has been notified and we will automatically apply this discount to your next tour booking.</p>
                        <Link href="/destinations">
                            <Button size="lg" className="bg-primary hover:bg-primary/90 text-white font-medium px-8 h-12">
                                Explore Tours Now
                            </Button>
                        </Link>
                    </div>
                ) : (
                    <div className="bg-white p-12 rounded-lg shadow-sm border border-gray-100 shadow-xl">
                        <h2 className="text-3xl font-bold text-gray-900 mb-4">You're almost there, {name}!</h2>
                        <p className="text-gray-600 mb-8 max-w-xl mx-auto">Click the button below to officially claim your 20% off birthday gift. You can use it on any of our Safaris within the next 30 days.</p>

                        <Button
                            size="lg"
                            onClick={handleRedeem}
                            disabled={isRedeeming}
                            className="bg-blue-600 hover:bg-blue-700 text-white font-medium px-12 h-14 text-lg w-full md:w-auto transition-all"
                        >
                            {isRedeeming ? "Processing..." : "Redeem 20% Off Offer"}
                        </Button>
                    </div>
                )}
            </div>
        </>
    );
}
